import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'; // Added useCallback
import { get, getProfile } from './api';

const PermissionContext = createContext();

export const usePermissions = () => useContext(PermissionContext);

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // New state to track authentication

  const fetchUserPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissions([]);
        setUserRole(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      const profileResponse = await getProfile();
      if (!profileResponse || !profileResponse.success || !profileResponse.data || !profileResponse.data.user_id) {
        setPermissions([]);
        setUserRole(null);
        setIsAuthenticated(false);
        return;
      }

      setIsAuthenticated(true);
      const userId = profileResponse.data.user_id;

      const permissionsResponse = await get('/permissions/user/' + userId);
      if (permissionsResponse.success) {
        setUserRole(permissionsResponse.role);
        setPermissions(permissionsResponse.permissions.all || []);
      } else {
        // Fallback if permissions API fails for an authenticated user
        setPermissions([]);
        setUserRole(profileResponse.data.role || null); // Use role from profile if available
      }
    } catch (error) {
      console.error("Failed to fetch user permissions:", error);
      setPermissions([]);
      setUserRole(null);
      setIsAuthenticated(false);
      // Optionally, show a toast or notification about the error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  const hasPermission = (permission) => {
    if (!isAuthenticated || loading) {
      return false; // Cannot determine permissions if not authenticated or still loading
    }
    // Admin has all permissions
    if (userRole === 'admin') {
      return true;
    }
    // Check if the specific permission exists in the user's granted permissions
    return permissions.includes(permission);
  };

  return (
    <PermissionContext.Provider value={{ permissions, hasPermission, loading, userRole, isAuthenticated, fetchUserPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
};

